import React from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useEaseOutProgress } from 'hooks/use-ease-out-progress';
import styles from './style.module.scss';

export interface PriceTilesProps {
  medianPurchasePrice: number;
  medianRentPrice: number;
  daysOnMarket: number;
}

/**
 * Flat label/value/sub tiles, matching the design's simple stat tiles
 * (no info-icon tooltip or timestamp — those belong to the shared StatCard
 * used elsewhere, but the FAQ answer wants the plainer treatment).
 */
const PriceTiles = ({ medianPurchasePrice, medianRentPrice, daysOnMarket }: PriceTilesProps) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency } = useI18nFormatting();

  // Count up from 0 to each stat on mount, same ease-out curve as the gauge.
  const progress = useEaseOutProgress([medianPurchasePrice, medianRentPrice, daysOnMarket]);

  const tiles = [
    medianPurchasePrice > 0 && {
      key: 'buy',
      label: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_BUYTITLE, { defaultValue: 'Buy' }),
      value: formatCurrency(Math.round(medianPurchasePrice * progress)),
      sub: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_BUYPERIOD, { defaultValue: 'median home price' }),
    },
    medianRentPrice > 0 && {
      key: 'rent',
      label: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_RENTTITLE, { defaultValue: 'Rent' }),
      value: formatCurrency(Math.round(medianRentPrice * progress)),
      sub: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_RENTPERIOD, { defaultValue: 'median monthly rent' }),
    },
    daysOnMarket > 0 && {
      key: 'speed',
      label: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_SPEEDTITLE, { defaultValue: 'Speed' }),
      value: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_DAYS, { count: Math.round(daysOnMarket * progress), defaultValue: '{{count}} days' }),
      sub: t(LOCALE_COMMON_PATHS.MARKETFAQ_PRICETILES_SPEEDPERIOD, { defaultValue: 'to sell, on average' }),
    },
  ].filter(Boolean) as { key: string; label: string; value: string; sub: string }[];

  if (tiles.length === 0) {
    return null;
  }

  return (
    <div className={styles.grid}>
      {tiles.map(tile => (
        <div key={tile.key} className={styles.tile}>
          <div className={styles.label}>{tile.label}</div>
          <div className={styles.value}>{tile.value}</div>
          <div className={styles.sub}>{tile.sub}</div>
        </div>
      ))}
    </div>
  );
};

export default PriceTiles;
